import React, { useMemo, useState, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions as firebaseFunctions } from "../../firebase";
import { logAudit } from "../../utils/audit";
import { useToast } from "../../components/ui/ToastProvider";
import { mapFirebaseError } from "../../utils/userErrors";
import { useFamilyData } from "../../hooks/useFamilyData";

const BUILT_IN_TEMPLATES = [
  {
    id: "morning",
    label: "Morning Routine",
    chores: [
      { title: "Make bed", points: 3, frequency: "repeat", repeatType: "daily" },
      { title: "Brush teeth", points: 2, frequency: "repeat", repeatType: "daily" },
      { title: "Get dressed", points: 3, frequency: "repeat", repeatType: "daily" }
    ]
  },
  {
    id: "homework",
    label: "Homework Time",
    chores: [
      { title: "Homework", points: 8, frequency: "repeat", repeatType: "weekdays" },
      { title: "Pack backpack", points: 4, frequency: "repeat", repeatType: "weekdays" }
    ]
  },
  {
    id: "bedtime",
    label: "Bedtime Routine",
    chores: [
      { title: "Brush teeth", points: 2, frequency: "repeat", repeatType: "daily" },
      { title: "Put away toys", points: 3, frequency: "repeat", repeatType: "daily" },
      { title: "Pajamas on", points: 2, frequency: "repeat", repeatType: "daily" }
    ]
  }
];

export default function TaskCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { tasks = [], kids: kidsList = [] } = useFamilyData(familyId);
  const [newTask, setNewTask] = useState("");
  const [points, setPoints] = useState(0); 
  const [assignee, setAssignee] = useState("unassigned");
  const [frequency, setFrequency] = useState("once");
  const [repeatType, setRepeatType] = useState("daily");
  const [customDays, setCustomDays] = useState([]);
  const [repeatDay, setRepeatDay] = useState(1);
  const [repeatExceptions, setRepeatExceptions] = useState([]);
  const [exceptionDate, setExceptionDate] = useState("");
  const [skipToday, setSkipToday] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [expiryTime, setExpiryTime] = useState("");
  const [showAfterTime, setShowAfterTime] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [taskError, setTaskError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const confirmTimeoutRef = useRef(null);
  const createFamilyTasks = useMemo(
    () => httpsCallable(firebaseFunctions, "createFamilyTasks"),
    []
  );

  const isPro = familySettings?.isPro || false;
  const freeTaskLimit = 8;
  const activeTaskCount = tasks.filter((task) => {
    const status = task.status || "open";
    return status !== "completed" && status !== "closed";
  }).length;
  const presets = familySettings?.presetTimes || [];
  const templates = useMemo(() => {
    const customTemplates = (familySettings?.taskTemplates || []).map(t => {
      if (Array.isArray(t.chores) && t.chores.length > 0) {
        return {
          id: t.id,
          label: t.name || "Custom Template",
          chores: t.chores
        };
      }
      return {
        id: t.id,
        label: t.name || t.title || "Custom Template",
        chores: [
          {
            title: t.title || "",
            points: t.points || 0,
            frequency: t.frequency || "once",
            repeatType: t.repeatType || "daily",
            customDays: t.customDays || [],
            repeatDay: typeof t.repeatDay === "number" ? t.repeatDay : 1,
            expiryTime: t.expiryTime || "",
            showAfterTime: t.showAfterTime || "",
            gracePeriodMins: t.gracePeriodMins || 0
          }
        ]
      };
    });
    return [...BUILT_IN_TEMPLATES, ...customTemplates];
  }, [familySettings?.taskTemplates]);
  const selectedTemplate = useMemo(
    () => templates.find(t => t.id === templateId),
    [templates, templateId]
  );

  useEffect(
    () => () => {
      if (confirmTimeoutRef.current) {
        clearTimeout(confirmTimeoutRef.current);
      }
    },
    []
  );

  const triggerConfirmFeedback = (message) => {
    setConfirmMessage(message);
    setConfirmed(true);
    if (confirmTimeoutRef.current) {
      clearTimeout(confirmTimeoutRef.current);
    }
    confirmTimeoutRef.current = setTimeout(() => {
      setConfirmed(false);
      setConfirmMessage("");
    }, 2200);
  };

  const createTask = (targetId, override = {}) => {
    const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);
    const exceptions = isPro ? [...repeatExceptions] : [];
    if (isPro && skipToday && !exceptions.includes(todayStr)) exceptions.push(todayStr);
    const baseTask = {
      title: override.title ?? newTask,
      points: Number(override.points ?? points) || 0,
      assignedTo: targetId,
      status: "open",
      frequency: override.frequency ?? frequency,
      expiryTime: isPro ? (override.expiryTime ?? expiryTime) : null,
      gracePeriodMins: 0,
      lastReset: new Date().setHours(0, 0, 0, 0),
      needsClarification: false,
      clarifications: []
    };

    const repeatFrequency = override.frequency ?? frequency;
    const repeatKind = override.repeatType ?? repeatType;
    if (repeatFrequency !== "once") {
      const repeat = { type: repeatKind };
      if (repeatKind === "custom") repeat.days = override.customDays ?? customDays;
      if (repeatKind === "weekly") repeat.day = override.repeatDay ?? repeatDay;
      if (isPro && exceptions.length > 0) repeat.exceptions = exceptions;
      baseTask.repeat = repeat;
      const showTimeValue = override.showAfterTime ?? showAfterTime;
      baseTask.showAfterTime = showTimeValue ? showTimeValue : null;
    }

    return baseTask;
  };

  const resolveAssignee = () => {
    if (assignee !== "auto") return assignee;
    const eligible = kidsList.filter(k => !k.isFrozen);
    if (eligible.length === 0) return "unassigned";
    const sorted = [...eligible].sort((a, b) => (a.points || 0) - (b.points || 0));
    return sorted[0].id;
  };

  const handleAddTask = async () => {
    if (isSubmitting) return;
    if (!newTask.trim()) {
      setTaskError("Chore name is required.");
      return;
    }
    setTaskError("");
    setIsSubmitting(true);
    const finalAssignee = resolveAssignee();
    const payloads =
      assignee === "all"
        ? kidsList.map((kid) => createTask(kid.id))
        : [createTask(finalAssignee)];

    try {
      await createFamilyTasks({
        familyId,
        tasks: payloads
      });
    } catch (error) {
      setTaskError(mapFirebaseError(error, "Unable to create chores."));
      setIsSubmitting(false);
      return;
    }
    void logAudit({
      familyId,
      action: "create",
      entity: "task",
      meta: { title: newTask }
    }).catch(() => {
      // Audit write failures should not block chore creation UX.
    });

    setNewTask("");
    setPoints(0);
    setExpiryTime("");
    setRepeatType("daily");
    setCustomDays([]);
    setRepeatDay(1);
    setRepeatExceptions([]);
    setExceptionDate("");
    setSkipToday(false);
    setTemplateId("");
    setShowAfterTime("");
    triggerConfirmFeedback(payloads.length > 1 ? "Chores added for all kids." : "Chore assigned.");
    showToast({ type: "success", message: "Chore assigned." });
    setIsSubmitting(false);
  };

  const toggleCustomDay = (day) => {
    setCustomDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addExceptionDate = () => {
    if (!exceptionDate) return;
    setRepeatExceptions(prev => (prev.includes(exceptionDate) ? prev : [...prev, exceptionDate]));
    setExceptionDate("");
  };

  const applyTemplate = (id) => {
    const template = templates.find(t => t.id === id);
    setTemplateId(id);
    if (!template) return;
    const first = template.chores[0];
    if (!first) return;
    setNewTask(first.title || "");
    setPoints(first.points || 0);
    setFrequency(first.frequency || "once");
    setRepeatType(first.repeatType || "daily");
    setCustomDays(first.customDays || []);
    setRepeatDay(typeof first.repeatDay === "number" ? first.repeatDay : 1);
    setExpiryTime(first.expiryTime || "");
    setShowAfterTime(first.showAfterTime || "");
  };

  const addTemplateTasks = async () => {
    if (isSubmitting) return;
    if (!selectedTemplate || selectedTemplate.chores.length === 0) return;
    setTaskError("");
    setIsSubmitting(true);
    const payloads = [];
    for (const chore of selectedTemplate.chores) {
      if (assignee === "all") {
        for (const kid of kidsList) {
          payloads.push(createTask(kid.id, chore));
        }
      } else {
        const finalAssignee = resolveAssignee();
        payloads.push(createTask(finalAssignee, chore));
      }
    }
    try {
      await createFamilyTasks({
        familyId,
        tasks: payloads
      });
    } catch (error) {
      setTaskError(mapFirebaseError(error, "Unable to apply template chores."));
      setIsSubmitting(false);
      return;
    }
    void logAudit({
      familyId,
      action: "create",
      entity: "taskTemplate",
      meta: { templateId: selectedTemplate.id }
    }).catch(() => {
      // Audit write failures should not block template create UX.
    });
    triggerConfirmFeedback("Template chores added.");
    showToast({ type: "success", message: "Template chores added." });
    setIsSubmitting(false);
  };

  return (
    <div className="glass parent-card task-card" data-tour="parent-task-card">
      <h2 className={confirmed ? "task-title-confirmed" : ""}>
        {confirmed ? "✅ Assigned!" : "Assign Chores"}
      </h2>
      <div className="card-body">
        {confirmed && (
          <div className="task-added-banner" role="status" aria-live="polite">
            {confirmMessage || "Chore assigned."}
          </div>
        )}
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>
            You have view-only access.
          </p>
        )}
        {canManage && (
        <>
        {isPro && (
          <select value={templateId} onChange={(e) => applyTemplate(e.target.value)} style={{ marginBottom: '10px' }}>
            <option value="">Load Template (optional)</option>
            {templates.map(t => (
              <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        )}
        {isPro && selectedTemplate && selectedTemplate.chores.length > 1 && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '6px' }}>
              Template chores ({selectedTemplate.chores.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedTemplate.chores.map((chore, index) => (
                <span key={`${chore.title}-${index}`} className="template-chip">
                  {chore.title}
                </span>
              ))}
            </div>
            <button className="btn-nav" onClick={addTemplateTasks} disabled={isSubmitting} style={{ width: '100%', marginTop: '8px' }}>
              {isSubmitting ? "Adding..." : "Add All Template Chores"}
            </button>
          </div>
        )}
        {!isPro && (
          <small style={{ opacity: 0.75, display: "block", marginBottom: 6 }}>
            Free limit: {activeTaskCount}/{freeTaskLimit} active chores.
          </small>
        )}
        <input
          data-tour="task-title-input"
          placeholder="Chore Name"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
        />
        {taskError && (
          <small className="pin-error" role="alert">
            {taskError}
          </small>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <input type="number" placeholder="Pts" value={points} onChange={(e) => setPoints(e.target.value)} style={{ width: '80px' }} />
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} style={{ flex: 1 }}>
            <option value="unassigned">Up for Grabs</option>
            {isPro && <option value="auto">Auto-Assign (Pro)</option>}
            <option value="all">All Kids</option>
            {kidsList.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
        </div>
        
        <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={{ marginTop: '10px', width: '100%' }}>
            <option value="once">Once</option>
            <option value="repeat">Repeat</option>
        </select>

        {frequency === "repeat" && (
          <select value={repeatType} onChange={(e) => setRepeatType(e.target.value)} style={{ marginTop: '10px', width: '100%' }}>
            <option value="daily">Daily</option>
            <option value="weekdays">Weekdays (Mon-Fri)</option>
            <option value="weekends">Weekends (Sat-Sun)</option>
            <option value="weekly">Weekly (Specific Day)</option>
            <option value="custom">Custom Days</option>
          </select>
        )}

        {frequency === "repeat" && (
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>
              Show time (repeat chores only)
            </label>
            <input
              type="time"
              value={showAfterTime}
              onChange={(e) => setShowAfterTime(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
        )}

        {frequency === "repeat" && isPro && (
          <div style={{ marginTop: '10px', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', padding: '10px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={skipToday}
                onChange={(e) => setSkipToday(e.target.checked)}
              />
              Skip today for this task
            </label>

            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <input
                type="date"
                value={exceptionDate}
                onChange={(e) => setExceptionDate(e.target.value)}
                style={{ marginBottom: 0 }}
              />
              <button className="btn-nav" onClick={addExceptionDate}>Add Exception</button>
            </div>

            {repeatExceptions.length > 0 && (
              <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {repeatExceptions.map(date => (
                  <button
                    key={date}
                    className="btn-nav"
                    onClick={() => setRepeatExceptions(prev => prev.filter(d => d !== date))}
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                  >
                    {date} ×
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {frequency === "repeat" && repeatType === "custom" && (
          <div style={{ marginTop: '10px', padding: '10px', border: '1px solid var(--accent)', borderRadius: '4px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem' }}>Select days: </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, dayNum) => (
                <button
                  key={dayNum}
                  onClick={() => toggleCustomDay(dayNum)}
                  style={{
                    padding: '8px',
                    backgroundColor: customDays.includes(dayNum) ? 'var(--accent)' : 'var(--surface)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: customDays.includes(dayNum) ? 'bold' : 'normal'
                  }}
                >
                  {dayName}
                </button>
              ))}
            </div>
          </div>
        )}

        {frequency === "repeat" && repeatType === "weekly" && (
          <select value={repeatDay} onChange={(e) => setRepeatDay(Number(e.target.value))} style={{ marginTop: '10px', width: '100%' }}>
            {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((dayName, dayNum) => (
              <option key={dayName} value={dayNum}>{dayName}</option>
            ))}
          </select>
        )}

        {isPro && (
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>
              Due time (optional)
            </label>
            <select onChange={(e) => setExpiryTime(e.target.value)} value={expiryTime} style={{ width: '100%', marginBottom: '5px' }}>
              <option value="">Select preset...</option>
              {presets.map(p => <option key={p.id} value={p.time}>{p.name} ({p.time})</option>)}
            </select>
            <input
              type="time"
              value={expiryTime}
              onChange={(e) => setExpiryTime(e.target.value)}
              style={{ width: '100%' }}
              aria-label="Due time"
            />
          </div>
        )}

        <button
          className={`btn-nav success-btn${confirmed ? " task-added-button" : ""}`}
          onClick={handleAddTask}
          disabled={isSubmitting}
          style={{ width: '100%', marginTop: '10px' }}
        >
          {isSubmitting ? "Assigning..." : "Assign"}
        </button>
        </>
        )}
      </div>
    </div>
  );
}
